import type { ReactNode } from "react";

import type { Tone } from "@/lib/integrations/words";

import styles from "./integrations.module.css";

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

const ICON_TONE: Record<Tone, string> = {
  neutral: "",
  good: styles.tlGood,
  warning: styles.tlWarning,
  danger: styles.tlDanger,
  info: styles.tlInfo,
};

/** Things that happened, newest first, one card each with a gap between. */
export function Timeline({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.tlWrap}>
      <ol className={styles.tl} aria-label={label}>
        {children}
      </ol>
    </div>
  );
}

/**
 * One thing on a timeline: a status icon, a few labelled cells, actions on the
 * right, and room under it for details. `flagged` marks the one a person should
 * look at; `compact` is one cell wide.
 */
export function TimelineItem({
  icon,
  tone = "neutral",
  flagged = false,
  compact = false,
  cells,
  actions,
  detail,
}: {
  icon: ReactNode;
  tone?: Tone;
  flagged?: boolean;
  compact?: boolean;
  cells: { key: string; label?: string; content: ReactNode }[];
  actions?: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <li className={cx(styles.tlItem, compact && styles.tlVersion, flagged && styles.tlNotRead)}>
      <span className={cx(styles.tlIcon, ICON_TONE[tone])}>{icon}</span>
      {cells.map((cell) => (
        <div key={cell.key} className={styles.tlCell}>
          {cell.label && <span className={styles.tlLabel}>{cell.label}</span>}
          {cell.content}
        </div>
      ))}
      <div className={styles.tlActions}>{actions}</div>
      {detail && <div className={styles.tlDetail}>{detail}</div>}
    </li>
  );
}

export function TimelineMain({ children }: { children: ReactNode }) {
  return <span className={styles.tlMain}>{children}</span>;
}

export function TimelineSub({ children }: { children: ReactNode }) {
  return <span className={styles.tlSub}>{children}</span>;
}

/** Pills and words on one wrapping line. */
export function TimelineLine({ children }: { children: ReactNode }) {
  return <span className={styles.tlSay}>{children}</span>;
}

/** Several of omnium's runs under one Scout run, spaced apart. */
export function TimelineStack({ children }: { children: ReactNode }) {
  return <div className={styles.tlOurs}>{children}</div>;
}

export function TimelineOne({ children }: { children: ReactNode }) {
  return <div className={styles.tlOne}>{children}</div>;
}
